import { CopyIntegrationPromptButton } from '../../components/CopyIntegrationPromptButton';
import { buildIntegrationPrompt } from '../../lib/integrationPrompt';
import { H1, P, Code } from './primitives';

export function SectionIntegrationApi() {
  return (
    <>
      <H1>Integration API</H1>
      <P>
        Let another website create a <strong>brand-new prototype</strong> for each visitor,
        follow it until it&rsquo;s live and file the visitor&rsquo;s feedback on it. A workspace
        owner creates the token under <strong>Workspace settings → Integration tokens</strong>{' '}
        (shown once, starts with <Code>aiai_int_</Code>); keep it on the site&rsquo;s server.
      </P>
      <P>
        The reference below is also a ready-made prompt: copy it into the AI coding tool that
        builds your other site. It contains this platform&rsquo;s URL but never your token.
      </P>
      <P>
        Every <Code>403</Code> carries a machine-readable <Code>code</Code>; branch on it, never on
        the message. Only <Code>PROTOTYPE_LIMIT_REACHED</Code> (the workspace is at its prototype
        cap, with <Code>count</Code>, <Code>max</Code> and <Code>plan</Code>) means &ldquo;full, try
        later&rdquo;. <Code>FEATURE_DISABLED</Code>, <Code>INSUFFICIENT_SCOPE</Code> (with{' '}
        <Code>requiredScope</Code>) and <Code>WRONG_TOKEN_KIND</Code> are misconfigurations.
      </P>
      <P>
        When the platform has an LLM configured, public inbound text is screened for spam, abuse and
        empty content before it costs a Product Manager turn. Junk requests and feedback are still
        stored, but closed on arrival (tag <Code>screened</Code>; a request&rsquo;s status is{' '}
        <Code>dismissed</Code>), and a junk <Code>POST /prototypes</Code> brief is refused with{' '}
        <Code>400</Code> <Code>BRIEF_REJECTED</Code> before anything is created.
      </P>
      <P>
        A full-stack factory prototype also gets a one-time <strong>setup code</strong> (e.g.{' '}
        <Code>K7P-M3Q-W9X</Code>). The prototype object&rsquo;s <Code>admin</Code> field carries the
        app&rsquo;s admin URL, that code and <Code>setupRequired</Code> (whether the app&rsquo;s
        owner login exists yet; <Code>null</Code> while unknown, and <Code>admin</Code> is{' '}
        <Code>null</Code> for a static app). Email the code with the live link: whoever creates the
        app&rsquo;s owner login must enter it, and that account is the visitor&rsquo;s own step.
        In the app the code sits under the prototype&rsquo;s <strong>Settings → App admin
        account</strong>, and as the visible <Code>AIAI_SETUP_CODE</Code> entry under{' '}
        <strong>Environment secrets</strong>. Entries there are hidden secrets unless marked{' '}
        <strong>Visible (not a secret)</strong>; platform keys (<Code>JWT_SECRET</Code>,{' '}
        <Code>AIAI_FEEDBACK_*</Code>, <Code>S3_*</Code>) can never be made visible.
      </P>
      <P>
        After v1 the prototype object also lists <Code>upcoming</Code>: up to 10 items on their way
        to the live app, each <Code>planned</Code> or <Code>building</Code>, with a plain-language
        title for visitors. The agents write that title when they file or plan the work (in the
        prototype&rsquo;s locale); items without one are left out but still counted in{' '}
        <Code>iteration</Code>. A new or moved item fires <Code>prototype.status_changed</Code>.
      </P>
      <div className="mb-4">
        <CopyIntegrationPromptButton className="btn-primary" />
      </div>
      <pre className="max-h-[36rem] overflow-auto whitespace-pre-wrap break-words rounded border border-border bg-surface-2 p-4 font-mono text-xs leading-relaxed text-ink">
        {buildIntegrationPrompt(window.location.origin)}
      </pre>
      <H1>End-user feedback</H1>
      <P>
        A prototype or a repo project can take feedback straight from its own app&rsquo;s users.
        It runs on the same Integration API, through a narrow, write-only token: scope{' '}
        <Code>feedback:write</Code>, which can file feedback and read nothing else. An owner ticks{' '}
        <strong>End-user feedback</strong> (on the prototype page, or <strong>Team</strong> for a
        repo project). That mints the token, which is also listed under Integration tokens.
        Rotating replaces it, and turning it off revokes it at once.
      </P>
      <P>
        A prototype gets <Code>AIAI_FEEDBACK_TOKEN</Code> and <Code>AIAI_FEEDBACK_URL</Code>{' '}
        injected into its serve container. For a repo project, set them on your app&rsquo;s server
        from the card. The server posts <Code>{'{ body, context?: { path, label } }'}</Code> to{' '}
        <Code>POST /api/integrations/v1/feedback</Code> with{' '}
        <Code>Authorization: Bearer </Code>, at most 20 a minute per token.
        Each submission lands on the board as a proposed <Code>feedback</Code> ticket for the
        Product Manager.
      </P>
      <P>
        Keep the token server-side: the browser posts to a route on your own server, which forwards
        the submission. Never put the token in browser code. The old{' '}
        <Code>/api/prototype-feedback</Code> URL still works for containers that haven&rsquo;t been
        redeployed yet. It is deprecated and will be removed later.
      </P>
      <P>
        <strong>Send feedback to aiai-loop</strong> (in the top bar) is separate: it is feedback
        about this platform itself, not from your app&rsquo;s users.
      </P>
    </>
  );
}
